import React, { useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Upload, Download, FileSpreadsheet, RefreshCw, PlusCircle, AlertTriangle, CheckCircle2, X, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── canonical import/export columns ──────────────────────────────────────────
// label = the CSV header we WRITE (export) and prefer on read; field = the
// order attribute; kind drives parsing + validation; aliases let a hand-made
// sheet use the snake_case field name (or a couple of common variants) too.
export const ECO_COLS = [
  { field: "organisation_name",    label: "Organisation",        kind: "text", required: true,  aliases: ["client", "organisation", "org"] },
  { field: "order_type",           label: "Order Type",          kind: "type", required: true,  aliases: ["type"] },
  { field: "po_number",            label: "PO Number",           kind: "text", aliases: ["po", "po no", "po#"] },
  { field: "po_value_basic",       label: "PO Value",            kind: "num",  aliases: ["po value (basic)", "po value basic", "booked", "value"] },
  { field: "po_received_date",     label: "PO Received Date",    kind: "date", aliases: ["po date", "po received"] },
  { field: "advance_confirm_date", label: "Advance Confirm Date",kind: "date", aliases: ["advance date"] },
  { field: "dispatch_status",      label: "Dispatch Status",     kind: "text", aliases: [] },
  { field: "invoice_number",       label: "Invoice Number",      kind: "text", aliases: ["invoice no", "inv no", "inv#"] },
  { field: "invoice_date",         label: "Invoice Date",        kind: "date", aliases: ["inv date"] },
  { field: "invoiced_value_basic", label: "Invoiced Value",      kind: "num",  aliases: ["invoiced value (basic)", "invoiced"] },
  { field: "comments",             label: "Comments",            kind: "text", aliases: ["comment", "remarks"] },
  { field: "payment_status",       label: "Payment Status",      kind: "text", aliases: [] },
  { field: "followup_needed",      label: "Follow-up",           kind: "bool", aliases: ["followup", "follow up", "follow-up needed"] },
  { field: "followup_note",        label: "Follow-up Note",      kind: "text", aliases: ["followup note"] },
  { field: "total_value_incl",     label: "Total Value",         kind: "num",  aliases: ["total value (incl)", "total (incl)", "total"] },
  { field: "received_so_far",      label: "Received",            kind: "num",  aliases: ["received so far", "collected"] },
  { field: "deadline",             label: "Deadline",            kind: "date", aliases: ["payment deadline", "due"] },
  { field: "stage",                label: "Stage",               kind: "text", aliases: [] },
];

const TYPE_CANON = { project: "Project", "suppl.": "Supplementary", suppl: "Supplementary", supplementary: "Supplementary", demo: "Demo", "demo kit": "Demo", install: "Install", freight: "Freight" };
const ORDER_TYPES = ["Project", "Supplementary", "Demo", "Install", "Freight"];
const STAGES = ["PO Received", "Procurement", "Manufacturing", "Quality Control", "Dispatch", "Completed"];

const norm = (s) => String(s || "").trim().toLowerCase().replace(/[\s_·.#-]+/g, " ").trim();
const canonType = (v) => TYPE_CANON[norm(v).replace(/ /g, "")] || TYPE_CANON[norm(v)] || (ORDER_TYPES.find((t) => norm(t) === norm(v)) || String(v || "").trim());
const num = (v) => { const n = parseFloat(String(v).replace(/,/g, "")); return Number.isFinite(n) ? n : 0; };
const isISO = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v).trim());
const boolp = (v) => /^(y|yes|true|1)/i.test(String(v).trim());

// ── CSV parse (quote-aware; mirrors the ERP ImportModal) ──────────────────────
function parseCSV(text) {
  const lines = String(text).split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseRow = (line) => {
    const out = []; let cur = "", q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (ch === "," && !q) { out.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };
  return { headers: parseRow(lines[0]), rows: lines.slice(1).map(parseRow) };
}

// map a CSV header row → { field: colIndex } using label/field/alias matching
function mapHeaders(headers) {
  const map = {}; const present = new Set();
  headers.forEach((h, i) => {
    const n = norm(h);
    const col = ECO_COLS.find((c) => norm(c.label) === n || norm(c.field) === n || (c.aliases || []).some((a) => norm(a) === n));
    if (col && map[col.field] === undefined) { map[col.field] = i; present.add(col.field); }
  });
  return { map, present };
}

// ── CSV export ────────────────────────────────────────────────────────────────
const csvCell = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export function ordersToCSV(orders) {
  const head = ECO_COLS.map((c) => c.label).join(",");
  const body = (orders || []).map((o) => ECO_COLS.map((c) => {
    let v = o[c.field];
    if (c.kind === "bool") v = v ? "Yes" : "No";
    else if (c.kind === "date") v = v ? String(v).slice(0, 10) : "";
    else if (c.kind === "num") v = v == null ? "" : v;
    return csvCell(v);
  }).join(",")).join("\n");
  return head + "\n" + body;
}
export function downloadCSV(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
export function exportOrders(orders) {
  const stamp = new Date().toISOString().slice(0, 10);
  downloadCSV(`ecotile_orders_${stamp}.csv`, ordersToCSV(orders));
  toast.success(`Exported ${orders.length} order${orders.length === 1 ? "" : "s"}`, { description: `ecotile_orders_${stamp}.csv` });
}

// ── row → order body (only non-empty mapped fields, so a sparse CSV never
//    blanks existing data on update; create fills required defaults) ──
function rowToBody(row, map, present) {
  const raw = (f) => { const i = map[f]; return i === undefined ? "" : (row[i] || "").trim(); };
  const body = {}; const errors = [];
  ECO_COLS.forEach((c) => {
    if (!present.has(c.field)) return;
    const v = raw(c.field);
    if (v === "") return;
    if (c.kind === "num") { if (Number.isNaN(parseFloat(v.replace(/,/g, "")))) errors.push(`${c.label} must be a number`); else body[c.field] = num(v); }
    else if (c.kind === "date") { if (!isISO(v)) errors.push(`${c.label} must be YYYY-MM-DD`); else body[c.field] = v; }
    else if (c.kind === "bool") body[c.field] = boolp(v);
    else if (c.kind === "type") { const t = canonType(v); if (!ORDER_TYPES.includes(t)) errors.push(`Order Type "${v}" — use ${ORDER_TYPES.join(" / ")}`); else body[c.field] = t; }
    else body[c.field] = v;
  });
  if (body.stage && !STAGES.includes(body.stage)) errors.push(`Stage "${body.stage}" — use ${STAGES.join(" / ")}`);
  if (!raw("organisation_name")) errors.push("Missing Organisation");
  if (!raw("order_type")) errors.push("Missing Order Type");
  return { body, errors };
}

const keyOf = (po, type) => `${String(po || "").trim().toLowerCase()}||${String(type || "").trim().toLowerCase()}`;

export function EcotileImportModal({ orders, onClose, onImported }) {
  const [parsed, setParsed] = useState(null);   // { headers, rows, present }
  const [updateMode, setUpdateMode] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // existing orders keyed by (po, type) for create-vs-update matching
  const existing = useMemo(() => {
    const m = new Map();
    (orders || []).forEach((o) => { if (o.po_number) m.set(keyOf(o.po_number, o.order_type), o); });
    return m;
  }, [orders]);

  const cats = useMemo(() => {
    if (!parsed) return null;
    const news = [], updates = [], errs = [];
    parsed.rows.forEach((row, idx) => {
      const { body, errors } = rowToBody(row, parsed.map, parsed.present);
      const label = body.organisation_name || "(row " + (idx + 2) + ")";
      if (errors.length) { errs.push({ idx, label, errors }); return; }
      const match = body.po_number ? existing.get(keyOf(body.po_number, body.order_type)) : null;
      if (match) {
        const changed = Object.keys(body).filter((f) => f !== "organisation_name" && f !== "order_type" && String(match[f] ?? "").slice(0, 10) !== String(body[f] ?? "").slice(0, 10));
        updates.push({ idx, label, body, match, changed });
      } else news.push({ idx, label, body });
    });
    return { news, updates, errs };
  }, [parsed, existing]);

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) { toast.error("Please choose a .csv file (export gives you the right format)"); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSV(e.target.result);
      if (!headers.length) { toast.error("That file has no rows"); return; }
      const { map, present } = mapHeaders(headers);
      if (present.size === 0) { toast.error("No recognised columns — download the template to see the headers"); return; }
      setParsed({ headers, rows, map, present });
    };
    reader.readAsText(file);
  }, []);

  const doImport = async () => {
    if (!cats) return;
    const todo = [...cats.news.map((r) => ({ ...r, mode: "create" })), ...(updateMode ? cats.updates.filter((r) => r.changed.length).map((r) => ({ ...r, mode: "update" })) : [])];
    if (!todo.length) { toast.info("Nothing to import — no new rows" + (updateMode ? " or changes" : " (turn on ‘update existing’ to apply changes)")); return; }
    setBusy(true); setProgress({ done: 0, total: todo.length });
    let ok = 0, fail = 0;
    for (const r of todo) {
      try {
        if (r.mode === "create") await axios.post(`${API}/ecotile/orders`, r.body);
        else await axios.patch(`${API}/ecotile/orders/${r.match.id}`, r.body);
        ok++;
      } catch { fail++; }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setBusy(false);
    if (fail) toast.warning(`Imported ${ok}, ${fail} failed`);
    else toast.success(`Imported ${ok} order${ok === 1 ? "" : "s"}`);
    onImported && onImported();
    onClose();
  };

  const template = () => {
    const example = ["Rockpecker Demo Client, City", "Project", "PO-EXAMPLE-001", "420000", "2026-07-01", "", "Pending", "", "", "0", "sample row — delete me", "Pending", "No", "", "495600", "0", "", "PO Received"];
    downloadCSV("ecotile_import_template.csv", ECO_COLS.map((c) => c.label).join(",") + "\n" + example.map(csvCell).join(","));
  };

  const updChanging = cats ? cats.updates.filter((r) => r.changed.length).length : 0;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-auto" style={{ background: "rgba(0,0,0,.45)" }} onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape" && !busy) onClose(); }}>
      <div className="rl-frost w-full max-w-[640px] rounded-2xl border shadow-2xl mt-10 mb-10" onClick={(e) => e.stopPropagation()} data-testid="eco-import-modal">
        <div className="flex items-start justify-between px-5 py-3.5 border-b border-[hsl(var(--border))]">
          <div>
            <div className="font-bold text-[15px] flex items-center gap-2"><Upload className="w-4 h-4 text-[#B78E10]" /> Import orders</div>
            <div className="text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5">Bring in existing orders from a CSV — matched by PO number + type, so re-imports update rather than duplicate.</div>
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Close" className="w-7 h-7 rounded-full rl-well flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] text-[16px] leading-none disabled:opacity-40">×</button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {/* dropzone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed px-5 py-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[#E6B800] bg-[#FFCC00]/8" : "border-[hsl(var(--border))] hover:border-[#E6B800]/60 hover:bg-[hsl(var(--muted))]/30"}`}
            data-testid="eco-import-drop">
            <FileSpreadsheet className="w-7 h-7 mx-auto mb-2 text-[hsl(var(--muted-foreground))]" />
            <div className="text-[13px] font-semibold">{parsed ? `${parsed.rows.length} rows loaded — drop a new file to replace` : "Drop a CSV here, or click to choose"}</div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Headers match the export. <button type="button" onClick={(e) => { e.stopPropagation(); template(); }} className="underline hover:text-[hsl(var(--foreground))]">Download template</button></div>
            <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="eco-import-file" />
          </div>

          {cats && (
            <>
              {/* summary */}
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]"><PlusCircle className="w-3.5 h-3.5 text-emerald-600" /> New</div>
                  <div className="font-mono-data text-[19px] font-extrabold text-emerald-600 dark:text-emerald-400">{cats.news.length}</div>
                </div>
                <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]"><RefreshCw className="w-3.5 h-3.5 text-[#B78E10]" /> Update</div>
                  <div className="font-mono-data text-[19px] font-extrabold text-[#B78E10]">{updChanging}<span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]"> / {cats.updates.length} match</span></div>
                </div>
                <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]"><AlertTriangle className="w-3.5 h-3.5 text-red-500" /> Errors</div>
                  <div className="font-mono-data text-[19px] font-extrabold text-red-500">{cats.errs.length}</div>
                </div>
              </div>

              {/* update toggle */}
              <label className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-[hsl(var(--muted))]/40 cursor-pointer">
                <input type="checkbox" checked={updateMode} onChange={(e) => setUpdateMode(e.target.checked)} className="w-4 h-4" data-testid="eco-import-updatemode" />
                <span className="text-[12.5px]"><span className="font-semibold">Update existing orders</span> — apply changed fields to the {cats.updates.length} matched order{cats.updates.length === 1 ? "" : "s"}. Empty cells are left as-is (never blanked).</span>
              </label>

              {/* per-row preview (first 40) */}
              {(cats.errs.length > 0 || cats.updates.some((r) => r.changed.length)) && (
                <div className="rounded-lg border border-[hsl(var(--border))] max-h-52 overflow-auto divide-y divide-[hsl(var(--border))]/60 text-[12px]">
                  {cats.errs.slice(0, 40).map((r) => (
                    <div key={"e" + r.idx} className="px-3 py-1.5 flex items-start gap-2 bg-red-500/5">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
                      <span><span className="font-medium">{r.label}</span> <span className="text-red-600 dark:text-red-400">— {r.errors.join("; ")}</span></span>
                    </div>
                  ))}
                  {cats.updates.filter((r) => r.changed.length).slice(0, 40).map((r) => (
                    <div key={"u" + r.idx} className="px-3 py-1.5 flex items-start gap-2">
                      <RefreshCw className="w-3.5 h-3.5 text-[#B78E10] mt-0.5 shrink-0" />
                      <span><span className="font-medium">{r.label}</span> <span className="text-[hsl(var(--muted-foreground))]">— {r.changed.length} field{r.changed.length === 1 ? "" : "s"} change: {r.changed.slice(0, 4).map((f) => (ECO_COLS.find((c) => c.field === f) || {}).label || f).join(", ")}{r.changed.length > 4 ? "…" : ""}</span></span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-[hsl(var(--border))]">
          <div className="text-[11.5px] text-[hsl(var(--muted-foreground))]">
            {busy && progress ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress.done} / {progress.total}…</span>
              : cats ? `${cats.news.length} new${updateMode ? ` · ${updChanging} update` : ""} · ${cats.errs.length} skipped` : "No file loaded"}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy} className="h-9 px-3.5 rounded-full text-[12.5px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] disabled:opacity-40">Cancel</button>
            <button onClick={doImport} disabled={busy || !cats || (cats.news.length === 0 && (!updateMode || updChanging === 0))} data-testid="eco-import-run"
              className="h-9 px-4 rounded-full rl-lit-btn text-[12.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" style={{ backgroundColor: "#B78E10" }}>
              {busy ? "Importing…" : <>Import <ArrowRight className="w-3.5 h-3.5" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
